import { ActivityIndicator, StyleSheet, Text, View } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomAccordion from "../../../components/common/general/CustomAccordion/CustomAccordion";
import React from "react";
import { useGetAssessmentSubmissionAttemptHistoryQuery } from "../../../graphql/generated/graphql";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import ExplanationCard from "../../../components/assessment/ExplanationCard/ExplanationCard";

const AttemptHistoryTab = ({ assessmentId, userId }: { assessmentId: string; userId: string }) => {
  const { data, loading: isLoading } = useGetAssessmentSubmissionAttemptHistoryQuery({
    variables: {
      assessmentId,
      userId,
    },
  });

  const allAssessmentSubmissions = data?.getAttemptHistory?.data?.assessmentSubmissions;
  const attemptOrderById = new Map(
    [...(allAssessmentSubmissions ?? [])]
      .sort((a, b) => new Date(a?.created || "").getTime() - new Date(b?.created || "").getTime())
      .map((submission, index) => [submission?.id, index + 1])
  );

  const calculateTimeSpent = (startedAt: string, completedAt: string) => {
    const startedAtDate = new Date(startedAt || "");
    const completedAtDate = new Date(completedAt || "");
    const timeSpent = completedAtDate.getTime() - startedAtDate.getTime();
    const minutes = Math.floor(timeSpent / (1000 * 60));

    if (Number.isNaN(minutes)) {
      return "not available";
    }

    return `${minutes} min`;
  };

  const getScoreStyle = (score: number, maximumPoints: number) => {
    const percentage = maximumPoints > 0 ? (score / maximumPoints) * 100 : 0;

    if (percentage >= 75) {
      return {
        backgroundColor: "#D0F8AB",
        textColor: "#326212",
      };
    } else if (percentage >= 60) {
      return {
        backgroundColor: "#FEDF89",
        textColor: "#93370D",
      };
    } else {
      return {
        backgroundColor: "#FECCD6",
        textColor: "#C01048",
      };
    }
  };

  const getSelectedMcqChoices = (selectedChoiceIndices?: number[] | null, choices?: string[]) => {
    return (selectedChoiceIndices ?? [])
      .map((choiceIndex) => ({
        choiceIndex,
        choiceText: choices?.[choiceIndex],
      }))
      .filter((choice): choice is { choiceIndex: number; choiceText: string } => !!choice.choiceText);
  };

  return (
    <View style={styles.container}>
      <CustomSpacing size="xl" />
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#0000ff" />
        </View>
      ) : (
        <View style={styles.questionsContainer}>
          {allAssessmentSubmissions?.map((submission, index) => (
            <View key={submission.id}>
              <CustomText
                text="attempt_number"
                size="s"
                weight="ultraBold"
                interpolation={{ attemptNumber: attemptOrderById.get(submission.id) ?? index + 1 }}
                style={{ color: "#344054" }}
              />
              <CustomSpacing size="s" />
              <View style={styles.attemptContainer}>
                <CustomAccordion
                  isExpanded={false}
                  title={
                    <View style={styles.infoBox}>
                      <View style={styles.infoPanel}>
                        <View style={styles.infoItem}>
                          <CustomText text="date_taken" size="s" weight="ultraBold" style={{ color: "#344054" }} />
                          <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
                            <CustomText
                              text={new Date(submission?.startedAt || "").toLocaleDateString()}
                              size="s"
                              weight="normal"
                              useTranslationText={false}
                              style={{ color: "#344054" }}
                            />
                            <CustomText text="at" size="s" />
                            <CustomText
                              text={new Date(submission?.startedAt || "").toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                              size="s"
                              weight="normal"
                              useTranslationText={false}
                              style={{ color: "#344054" }}
                            />
                          </View>
                        </View>
                        <View style={styles.infoItem}>
                          {submission?.hasTeacherAdjustedGrade ? (
                            <CustomText
                              text="total_score_adjusted_by_faculty"
                              size="s"
                              weight="ultraBold"
                              style={{ color: "#344054" }}
                            />
                          ) : (
                            <CustomText text="total_score" size="s" weight="ultraBold" style={{ color: "#344054" }} />
                          )}
                          <View
                            style={[
                              styles.scorePill,
                              {
                                backgroundColor: getScoreStyle(
                                  submission?.totalPoints ?? 0,
                                  submission?.maximumPoints ?? 0
                                ).backgroundColor,
                              },
                            ]}>
                            <CustomText
                              text={`${submission?.totalPoints ?? 0}/${submission?.maximumPoints ?? 0}`}
                              size="s"
                              weight="bold"
                              useTranslationText={false}
                              style={{
                                color: getScoreStyle(submission?.totalPoints ?? 0, submission?.maximumPoints ?? 0)
                                  .textColor,
                              }}
                            />
                          </View>
                        </View>
                        <View style={styles.infoItem}>
                          <CustomText text="time_spent" size="s" weight="ultraBold" style={{ color: "#344054" }} />
                          <CustomText
                            text={calculateTimeSpent(submission?.startedAt || "", submission?.completedAt || "")}
                            size="s"
                            weight="normal"
                            useTranslationText={false}
                            style={{ color: "#344054" }}
                          />
                        </View>
                      </View>
                    </View>
                  }>
                  <View style={styles.divider} />
                  <CustomSpacing size="l" />
                  <View style={styles.answerSection}>
                    {(submission?.mcqAnswersConnection?.edges ?? []).length > 0
                      ? [...(submission?.mcqAnswersConnection?.edges ?? [])]
                          .sort((a, b) => (a?.node?.order ?? 0) - (b?.node?.order ?? 0))
                          .map((mcqAnswer, questionIndex) => {
                            const selectedChoices = getSelectedMcqChoices(
                              mcqAnswer?.properties?.selectedChoiceIndices,
                              mcqAnswer?.node?.choices
                            );

                            return (
                              <View key={mcqAnswer?.node?.id}>
                                <Text>
                                  <Text style={styles.questionTextTitle}>{`Question ${questionIndex + 1}: `}</Text>
                                  <Text style={styles.questionText}>{mcqAnswer?.node?.question || ""}</Text>
                                </Text>
                                <CustomSpacing size="s" />
                                <CustomText
                                  text="student_answer"
                                  size="s"
                                  weight="ultraBold"
                                  style={{ color: "#101828" }}
                                />
                                <CustomSpacing size="l" />
                                {selectedChoices.length > 0 ? (
                                  <>
                                    {selectedChoices.map((choice) => (
                                      <View key={`${mcqAnswer?.node?.id}-answer-${choice.choiceIndex}`}>
                                        <CustomText
                                          text={`${String.fromCharCode(65 + choice.choiceIndex)}. ${choice.choiceText}`}
                                          size="s"
                                          weight="normal"
                                          useTranslationText={false}
                                          style={{ color: "#101828" }}
                                        />
                                      </View>
                                    ))}
                                    {mcqAnswer?.node?.explanation && submission?.status === "COMPLETED" && (
                                      <ExplanationCard explanation={mcqAnswer?.node?.explanation} />
                                    )}
                                    <CustomSpacing size="xs" />
                                  </>
                                ) : (
                                  <CustomText
                                    text="No answer provided"
                                    size="s"
                                    weight="normal"
                                    useTranslationText={false}
                                    style={{ color: "#101828" }}
                                  />
                                )}
                                <CustomSpacing size="xl" />
                              </View>
                            );
                          })
                      : [...(submission?.questionsConnection?.edges ?? [])]
                          .sort((a, b) => {
                            const aHasAnswer = a?.properties?.answer && a.properties.answer.trim() !== "";
                            const bHasAnswer = b?.properties?.answer && b.properties.answer.trim() !== "";

                            if (aHasAnswer && !bHasAnswer) return -1;
                            if (!aHasAnswer && bHasAnswer) return 1;
                            return 0;
                          })
                          .map((question, questionIndex) => (
                            <View key={question.node?.id}>
                              <Text>
                                <Text style={styles.questionTextTitle}>{`Question ${questionIndex + 1}: `}</Text>
                                <Text style={styles.questionText}>{question?.node?.question || ""}</Text>
                              </Text>
                              <CustomSpacing size="s" />
                              <CustomText
                                text="student_answer"
                                size="s"
                                weight="ultraBold"
                                style={{ color: "#101828" }}
                              />
                              <CustomSpacing size="l" />
                              <CustomText
                                text={question?.properties?.answer || ""}
                                size="s"
                                weight="normal"
                                useTranslationText={false}
                                style={{ color: "#101828" }}
                              />
                              <CustomSpacing size="xl" />
                            </View>
                          ))}
                  </View>
                </CustomAccordion>
              </View>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

export default AttemptHistoryTab;

const styles = StyleSheet.create({
  infoBox: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "95%",
    gap: 10,
  },
  infoPanel: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 10,
  },
  infoItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  scorePill: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: "360px",
  },
  questionsContainer: {
    gap: 20,
    width: "100%",
  },
  answerSection: {
    gap: 8,
    paddingVertical: 4,
    paddingHorizontal: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300,
    minWidth: "100%",
    width: "100%",
    paddingHorizontal: 32,
  },
  attemptContainer: {
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 16,
    paddingHorizontal: 10,
    paddingVertical: 0,
  },
  container: {
    flex: 1,
    overflow: "scroll",
    marginHorizontal: 32,
  },
  divider: {
    height: 1,
    backgroundColor: "#EAECF0",
    marginHorizontal: 16,
  },
  questionTextTitle: {
    fontFamily: "Inter",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "700",
    color: "#101828",
  },
  questionText: {
    fontFamily: "Inter",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "400",
    color: "#101828",
  },
});
